<script setup>
import { ref, computed ,onMounted, onBeforeUnmount, provide, watch} from "vue";
import { ElDropdown, ElDropdownItem, ElMessage } from "element-plus";
import ChangePasswordDialog from "./ChangePasswordDialog.vue";
import SwitchRoleDialog from "./SwitchRoleDialog.vue";
import { ArrowDown } from '@element-plus/icons-vue'
import { useRouter, useRoute } from "vue-router";
import { useUserStore } from "@/store/user";
import { removeToken ,getImageUrl, getToken} from "@/util/common";
import { loginoutApi, changePasswordApi } from "@/api/sysadmin";
import { WS_BASE } from "@/api/im.js";
import { PLATFORM_NAME, VERSION } from "@/util/variable";
import { enumConfig } from "@/util/enum/index.js";
import platformUrl from "@/assets/images/avatar_top_platform.png";
import investorUrl from "@/assets/images/avatar_top_investor.png";
import driverUrl from "@/assets/images/avatar_top_driver.png";
import supplierUrl from "@/assets/images/avatar_top_supplier.png";

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();


const userInfo = userStore.getUserInfo() 

const activeRoleValue = userStore.getActiveRoleValue()
const permissions = userInfo?.permissions || []

const changePasswordVisible = ref(false);

const hasChatPermission = permissions.includes('menu:user-chat:chat')

const avatarUrl = computed(() => {
  let newUrl = '';
  if (activeRoleValue === 20) {
    newUrl = platformUrl;
  } else if (activeRoleValue === 30) {
    newUrl = investorUrl;
  } else if (activeRoleValue === 40) {
    newUrl = driverUrl;
  } else if (activeRoleValue === 60) {
    newUrl = supplierUrl;
  }
  return newUrl;
}); 

const handleChangePassword = () => {
  changePasswordVisible.value = true;
};

const switchRoleVisible = ref(false);

const handleSwitchRole = () => {
  switchRoleVisible.value = true;
};



const menuRoutes = computed(() => {
  return userStore.routes || [];
});

const activeMenu = computed(() => {
  return route.path;
});

const handleLogout = () => {
  loginoutApi().then(res => {
  }).finally(() => {
    // 关闭 WebSocket 连接（不重连）
    disconnectWs();
    // 清除用户状态
    userStore.logout();
    // 清除缓存
    localStorage.clear();
    sessionStorage.clear();
    // router.replace("/login");
    window.location.href = "/login";
  });
};

const handleMenuSelect = (index) => {
  router.push(index);
};

// WebSocket 相关
const ws = ref(null);
const wsConnected = ref(false);
const totalUnreadCount = ref(0);
let reconnectTimer = null;
let reconnectDelay = 1000;
const maxDelay = 1000 * 1;
const maxReconnectAttempts = 3;
let reconnectAttempts = 0;
let errorReconnectAttempts = 0;
let timeoutReconnectAttempts = 0;
let isServerTimeoutClose = false;
let isServerErrorClose = false;
let heartbeatTimer = null;
const heartbeatInterval = 1000 * 59;
let isManualClose = false;

// WebSocket 连接
function connectWs() {
  isManualClose = false;
  if (ws.value) {
    ws.value.close();
    ws.value = null;
  }

  ws.value = new WebSocket(WS_BASE);
  const token = getToken();

  ws.value.onopen = () => {
    reconnectDelay = 1000;
    reconnectAttempts = 0;
    errorReconnectAttempts = 0;
    timeoutReconnectAttempts = 0;
    wsConnected.value = true;
    // 发送首帧认证
    const firstFrame = { token: token };
    console.log('Layout WebSocket connected, firstFrame');
    ws.value.send(JSON.stringify(firstFrame));
    // 启动心跳
    startHeartbeat();
  };

  ws.value.onmessage = (e) => {
    const d = JSON.parse(e.data);
    userStore.setWsData(d);
    handleWsMessage(d);
  };

  ws.value.onclose = () => {
    wsConnected.value = false;
    stopHeartbeat();
    if (isManualClose) {
      console.log('WebSocket onclose: 主动关闭，不再重连');
      return;
    }
    if (isServerTimeoutClose) {
      isServerTimeoutClose = false;
      if (timeoutReconnectAttempts >= maxReconnectAttempts) {
        console.log(`WebSocket 超时重连已达 ${maxReconnectAttempts} 次，停止重连`);
        return;
      }
      timeoutReconnectAttempts++;
      console.log(`WebSocket onclose: 服务端超时断开，第 ${timeoutReconnectAttempts} 次重连`);
      connectWs();
    } else if (isServerErrorClose) {
      isServerErrorClose = false;
      console.log('WebSocket onclose: 服务端错误断开，已在消息处理中触发重连');
    } else {
      scheduleReconnect();
    }
  };

  ws.value.onerror = () => {
    ws.value.close();
  };
}

// 处理 WebSocket 消息
function handleWsMessage(d) {
  if (d.type === 'connected') return;
  
  if (d.type === 'closed' && d.msg === '连接超时已断开，请重连') {
    wsConnected.value = false;
    isServerTimeoutClose = true;
    console.log('WebSocket 服务端通知连接超时，准备重连');
    ws.value.close();
    return;
  }
  
  if (d.type === 'error') {
    // 业务致命错误（如用户不存在、认证失败），重连无法解决，直接停止重连
    wsConnected.value = false;
    isManualClose = true;
    console.warn('WebSocket 服务端返回错误，停止重连：', d.msg);
    clearTimeout(reconnectTimer);
    stopHeartbeat();
    if (ws.value) {
      ws.value.close();
      ws.value = null;
    }
    return;
  }
  
  if (d.type === 'msg' || d.type === 'selfMsg') {
    totalUnreadCount.value++;
  } else if (d.type === 'roomListUpdate') {
    if (d.unreadCount !== undefined) {
      totalUnreadCount.value = d.unreadCount;
    }
  } else if (d.type === 'readSync') {
    totalUnreadCount.value = Math.max(0, totalUnreadCount.value - 1);
  }
}

// 清除未读消息数
function clearUnreadCount() {
  totalUnreadCount.value = 0;
}

// 心跳
function startHeartbeat() {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify({ type: 'ping' }));
    }
  }, heartbeatInterval);
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

// 主动断开 WebSocket（不重连）
function disconnectWs() {
  isManualClose = true;
  clearTimeout(reconnectTimer);
  stopHeartbeat();
  if (ws.value) {
    ws.value.close();
    ws.value = null;
  }
}

// 定时重连
function scheduleReconnect() {
  if (reconnectAttempts >= maxReconnectAttempts) {
    console.log(`WebSocket 重连已达 ${maxReconnectAttempts} 次，停止重连`);
    return;
  }
  reconnectAttempts++;
  console.log(`WebSocket 第 ${reconnectAttempts} 次重连`);
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    connectWs();
  }, maxDelay);
}

// 提供给子组件的 WebSocket 实例和方法
provide('wsInstance', {
  ws,
  wsConnected,
  connectWs,
  totalUnreadCount,
  clearUnreadCount
});
const hasSwitchRole = ref(false)
onMounted(() => {
  hasSwitchRole.value = localStorage.getItem('hasSwitchRole') === 'true'    
  switchRoleVisible.value = !hasSwitchRole.value
  console.log(hasChatPermission);
  if (hasSwitchRole.value && hasChatPermission) {
    // 初始化 WebSocket 连接
    // connectWs();
  }
  // 监听 401 等场景的断开通知
  // window.addEventListener('ws-disconnect', disconnectWs);
});

onBeforeUnmount(() => {
  // window.removeEventListener('ws-disconnect', disconnectWs);
  disconnectWs();
});
</script>

<template>
  <div class="layout">
    <div class="layout-header">
      <div class="header-left">
        <img src="@/assets/images/logo1.png" alt="logo" class="logo">
        <div class="logo-text">{{ PLATFORM_NAME }}</div>
      </div>
      <div class="header-right">
          <i class="yhd-icon-home pointer" @click="router.push('/')"></i>
          <div class="msg-icon-wrapper" v-if="hasChatPermission" @click="router.push('/user-chat/chat')">
            <i class="yhd-icon-msg pointer"></i>
            <span v-if="totalUnreadCount > 0" class="msg-badge"></span>
          </div>
          <el-dropdown>
          <span class="el-dropdown-link">   
            <div class="user-name">
              <!-- {{ enumConfig.roleTypeList.find(item => item.value == activeRoleValue)?.label || '用户' }} -->
              {{ userInfo.realName }}
            </div>
            <el-icon class="el-icon--right">
              <arrow-down style="color: #fff;" />
            </el-icon>       
            <div class="user-avatar">
              <el-avatar :src="avatarUrl" />
            </div>
          </span>
          <template #dropdown>
            <el-dropdown-menu>              
              <el-dropdown-item v-if="userInfo?.identities?.length>1" @click="handleSwitchRole">
                  <el-button link>切换角色</el-button>
              </el-dropdown-item>
              <el-dropdown-item @click="handleChangePassword">
                  <el-button link>修改密码</el-button>
              </el-dropdown-item>
              <el-dropdown-item @click="handleLogout">
                  <el-button link >退出</el-button>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    <div class="layout-content" v-if="hasSwitchRole">
      <div class="layout-side">
        <el-menu
          :default-active="activeMenu"
          class="el-menu-vertical"
          mode="vertical"
          background-color="#000000"
          text-color="#ffffff" 
          @select="handleMenuSelect"
        >
          <template v-for="item in menuRoutes" :key="item.path">
            <el-menu-item v-if="!item.redirect && !item.children && item?.visible" :index="item.path">
              <i :class="`menu-icon`">
                <img class="img" :src="getImageUrl(item.meta?.icon)" alt="">
              </i>
              <span>{{ item.meta?.title }}</span>
            </el-menu-item>
            <el-sub-menu
              v-else-if="item.children && item.children.length"
              :index="item.path"
            >
              <template #title>
                <i v-if="item.meta?.icon" :class="`menu-icon`">
                  <img class="img" :src="getImageUrl(item.meta?.icon)" alt="">
                </i>
                <i v-else class="menu-icon"></i>
                <span>{{ item.meta?.title }}</span>
              </template>
              <el-menu-item
                v-for="child in item.children.filter(child => child?.visible)"
                :key="child.path"
                :index="`${item.path}/${child.path}`"
              >
                <i v-if="child.meta?.icon" :class="`menu-icon`">
                  <img class="img" :src="getImageUrl(child.meta?.icon)" alt="">
                </i>
                <i v-else class="menu-icon menu-icon01"></i>
                <span>{{ child.meta?.title }}</span>
              </el-menu-item>
            </el-sub-menu>
          </template>
        </el-menu>
      </div>
      <div class="layout-main">
        <div class="title" v-if="route.path !== '/home' && route.meta?.title">{{ route.meta?.title }}</div>
        <div class="content" :class="{'content-full': route.path === '/home'}">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
  <ChangePasswordDialog v-model:visible="changePasswordVisible" />
  <SwitchRoleDialog v-model:visible="switchRoleVisible" :from-login="true" />
</template>

<style scoped lang="scss">
@use '@/assets/css/common/base.scss' as *;
.layout {
  height: 100vh;
  background: $backgroundColor;
  color: $baseTextColor;
  // background: url('@/assets/images/bg001.png') no-repeat top left;
  // background-size: 100% auto;
  .layout-header {
    height: $headerH;
    background: $headerBg;
    color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0.18rem;
    .header-left {
      font-size: 0.16rem;
      font-weight: bold;
      line-height: 1.2;
      display: flex;
      align-items: center;
      gap: 0.05rem;
      .logo {
        width: 0.25rem;
        height: 0.235rem;
        object-fit: cover;
      }
    }
    .header-right {
      display: flex;
      align-items: center;
      gap:0.14rem;
      .el-avatar{
        --el-avatar-size: 0.28rem;
        margin-left: 0.1rem;
      }
      .el-tooltip__trigger{
        display: flex;
        align-items: center;
        font-size: 0.14rem;
        gap: 0;
        .user-name{
          color: rgba(255, 255, 255, 0.7);
        }
      }
      .msg-icon-wrapper {
        position: relative;
        cursor: pointer;
        .msg-badge {
          position: absolute;
          top: -0.03rem;
          right: -0.03rem;
          min-width: 0.1rem;
          height: 0.1rem;
          line-height: 0.1rem;
          background-color: #1A6CFF;
          color: #fff;
          font-size: 0.12rem;
          border-radius: 0.1rem;
          text-align: center;
        }
      }

    }
   }
  .layout-content {
    height: calc(100vh - $headerH);
    display: flex;
  }
  .layout-side {
    width: $siderOpenW;
    min-width: 2rem;
    height: 100%;
    background: $siderBg;
    // box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
    // border-top: 1px solid #4f4f4f;
    // padding: 0.40rem 0.08rem 0.20rem 0.24rem;
    padding: 0.12rem 0.08rem 0 0.1rem;
    .el-menu {
      height: 100%;
      border: none;
      overflow: auto;
      background: $siderBg;
      --el-menu-item-height: auto;
      --el-menu-level-padding:0;
      --el-menu-item-font-size: 0.16rem;
      
      &::-webkit-scrollbar {
        width: 0px;
        height: 0px;
      }
      .el-sub-menu{
        width: 100%;
      }
      :deep(.el-menu-item),
      :deep(.el-sub-menu__title){
          width: 100%;
          height: auto;
          // font-size: 0.16rem;
          font-size: 0.14rem;
          line-height: 0.4rem;
          // padding: 0.16rem 0;
          background: $siderBg;
          &:hover,
          &.active{
            background:  rgba(40,46,64,0.8);
          }
          .menu-icon{
            flex: 0 0 0.43rem;
            display: flex;
            align-items: center;
            justify-content: center;
            .img{
                width: auto;
                max-width: 0.43rem;
                height: 0.18rem;
            }
            &.menu-icon01{
              height: 100%;
              &::before{
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0.2rem;
                  width: 1px;
                  height: 100%;
                  background-color: #636363;
                }
            }
          }
          span{
            display: block;
            width: calc(100% - 0.43rem - 0.12rem);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            &+.el-icon{
              font-size: 0.10rem;
              width: 0.16rem;
              right: 0.1rem;
            }
          } 
      }
      :deep(.el-sub-menu){
         :deep(.el-menu-item){
            line-height: 0.36rem;      
         }
      }
      
      :deep(.el-menu-item.is-active) {
        // background: linear-gradient( 270deg, #989292 0%, #2A2B2E 100%);
        // box-shadow: 2px 2px 4px 0px rgba(0,0,0,0.04);
        // border-radius: 0px 0px 0px 0px;
        // border: 1px solid #000000;
        // text-shadow: 0px 0px 1px rgba(0,0,0,1);
        color: rgba(255,255,255,0.8);
        background:  rgba(40,46,64,0.8);
        .menu-icon01{
            &::after{
               content: '';
                  position: absolute;
                  top: 15%;
                  left: 0.2rem;
                  width: 2px;
                  height: 70%;
                  background: #fff;
              }
          }
      }
    }
  }
  .layout-main {
    flex: 1;
    height: 100%;
    .title {
      display: flex;
      align-items: center;
      height: $crumbH;
      font-size: 0.16rem;
      font-weight:600;
      line-height: 1.2;
      padding: 0 0.24rem;
      // background: #fff;
      color: #4C4C4C;
      background:#fff url('@/assets/images/nav_bg01.png') no-repeat right center / auto 100%; 
    }
    .content {
      height: calc(100% - $crumbH);
      overflow: hidden;
      &.content-full{
        height: 100%;
      }
    }
    
  }
}
</style>
